import { toEnginePath } from '@/pages/User/pages/MergeRequests/mapMergeRequest'
import { DiffChangeDto } from '@/rtkApi/mddApiSliceGeneratedV2'

import { RebaseChoice, rebaseResolutionValue } from './rebaseBranch'

export type CherryPickElement = {
	code?: string
	conflicts?: DiffChangeDto[]
	message?: string
	missingDependencies?: number[]
	name?: string
	sourceHistoryId?: number | null
	sourceParentStructureId?: number | null
	status?: unknown
	structureId?: number
	targetHistoryId?: number | null
	targetParentStructureId?: number | null
}

export type CherryPickResolutions = Record<string, Record<string, unknown>>

export type CherryPickExecuteBody = {
	conflictResolutions?: CherryPickResolutions
	continueWithMissingDependencies: boolean
	expectedSourceHistoryIds: Record<string, number | null>
	expectedSourceParentIds: Record<string, number | null>
	expectedTargetHistoryIds: Record<string, number | null>
	expectedTargetParentIds: Record<string, number | null>
	sourceBranchId: number
	structureIds: number[]
}

const statusOf = (status: unknown) => (typeof status === 'string' ? status : '')

export const canCherryPickInto = (branch: {
	protectedBranch?: boolean
	status: string
}) => branch.status === 'ACTIVE' && !branch.protectedBranch

/**
 * Changes query for cherry-pick. The changes endpoint reports what changed on
 * `sourceBranchId` compared with `targetBranchId`, so the source is the branch
 * we pick from and the target is the branch we pick into.
 */
export const cherryPickChangesArgs = (
	intoBranchId: number,
	fromBranchId: number,
) => ({
	sourceBranchId: fromBranchId,
	targetBranchId: intoBranchId,
})

export const canMarkCherryPickRow = (
	category: 'added' | 'removed' | 'modified' | 'unchanged',
) => category === 'added' || category === 'modified'

export const isCherryPickBlocked = (result: {
	blockingMergeRequests?: unknown[] | null
	status?: unknown
}) =>
	(result.blockingMergeRequests?.length ?? 0) > 0 ||
	statusOf(result.status) === 'BLOCKED'

export const hasCherryPickConflicts = (element: CherryPickElement) =>
	(element.conflicts?.length ?? 0) > 0

export const splitCherryPickPreview = (elements: CherryPickElement[]) => {
	const kept: CherryPickElement[] = []
	const skipped: CherryPickElement[] = []
	for (const element of elements) {
		const status = statusOf(element.status)
		if (
			hasCherryPickConflicts(element) ||
			status === 'READY' ||
			status === 'CONFLICT'
		) {
			kept.push(element)
		} else {
			skipped.push(element)
		}
	}
	return {
		kept,
		missing: kept.filter(
			(element) => (element.missingDependencies?.length ?? 0) > 0,
		),
		skipped,
	}
}

export const isTransferredElement = (element: CherryPickElement) =>
	statusOf(element.status) === 'TRANSFERRED'

export const cherryPickFieldForPath = (
	element: CherryPickElement,
	enginePath: string,
): DiffChangeDto | undefined =>
	(element.conflicts ?? []).find(
		(conflict) =>
			conflict.fieldPath != null && toEnginePath(conflict.fieldPath) === enginePath,
	)

export const cherryPickResolutionValue = (
	change: Pick<DiffChangeDto, 'newValue' | 'oldValue'>,
	choice: RebaseChoice,
	customValue?: unknown,
) => rebaseResolutionValue(change, choice, customValue)

export const cherryPickSelectionsComplete = (
	elements: CherryPickElement[],
	selected: CherryPickResolutions,
) =>
	elements.filter(hasCherryPickConflicts).every((element) => {
		if (element.structureId == null) return false
		const chosen = selected[String(element.structureId)] ?? {}
		return (element.conflicts ?? [])
			.filter((conflict) => conflict.fieldPath)
			.every((conflict) =>
				Object.prototype.hasOwnProperty.call(chosen, conflict.fieldPath as string),
			)
	})

const idMap = (
	elements: CherryPickElement[],
	valueOf: (element: CherryPickElement) => number | null | undefined,
): Record<string, number | null> => {
	const map: Record<string, number | null> = {}
	for (const element of elements) {
		if (element.structureId == null) continue
		const value = valueOf(element)
		map[String(element.structureId)] = value == null ? null : value
	}
	return map
}

export const cherryPickExecuteRequest = ({
	continueWithMissingDependencies,
	elements,
	resolutions,
	sourceBranchId,
}: {
	continueWithMissingDependencies: boolean
	elements: CherryPickElement[]
	resolutions: CherryPickResolutions
	sourceBranchId: number
}): CherryPickExecuteBody => {
	const structureIds = elements
		.map((element) => element.structureId)
		.filter((id): id is number => id != null)
	const kept = new Set(structureIds.map(String))
	const conflictResolutions: CherryPickResolutions = {}
	for (const [structureId, chosen] of Object.entries(resolutions)) {
		if (kept.has(structureId) && Object.keys(chosen).length > 0) {
			conflictResolutions[structureId] = chosen
		}
	}
	return {
		conflictResolutions,
		continueWithMissingDependencies,
		expectedSourceHistoryIds: idMap(elements, (element) => element.sourceHistoryId),
		expectedSourceParentIds: idMap(
			elements,
			(element) => element.sourceParentStructureId,
		),
		expectedTargetHistoryIds: idMap(elements, (element) => element.targetHistoryId),
		expectedTargetParentIds: idMap(
			elements,
			(element) => element.targetParentStructureId,
		),
		sourceBranchId,
		structureIds,
	}
}
